import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

import { PeopleListBloc } from '@connect-services/client/blocs/people-list.bloc';
import { RoleListBloc } from '@connect-services/client/blocs/role-list.bloc';
import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { LicensesSelectComponent } from '@connect-services/client/components/management/licenses-select/licenses-select.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    AbstractUserFormComponent,
    UserFormMethodsInterface,
    UserFormType,
} from '@connect-services/client/pages/management/organization/user-edit/abstract-user-form.component';
import { OrganizationAccessPermissionsService } from '@connect-services/client/services/organization-access-permissions.service';
import { ProjectSettingsPermissionsService } from '@connect-services/client/services/project-settings-permissions.service';
import { RoleService } from '@connect-services/client/services/role.service';
import { InputNumberComponent } from '@connect-services/src/lib/components/input/input-number/input-number.component';
import { InputTextComponent } from '@connect-services/src/lib/components/input/text/input-text/input-text.component';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { License, Project, Role } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { FormService } from '@shared/client/services/form.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ComplexModalTemplate,
        InputNumberComponent,
        InputTextComponent,
        LicensesSelectComponent,
        RessourcesModule,
        SelectComponent,
    ],
    selector: 'app-user-create-modal',
    templateUrl: './user-create-modal.component.html',
})
export class UserCreateModalComponent extends AbstractUserFormComponent implements OnInit, UserFormMethodsInterface {
    public readonly projects: WritableSignal<SelectOption<Project>[]> = signal([]);
    public readonly roles: WritableSignal<SelectOption<Role>[]> = signal([]);

    protected readonly canEditProject = computed(() => this.projectSettingsPermissionsService.canEditProject());
    protected readonly canManageUser = computed(() => this.organizationAccessPermissionsService.canManageUser());
    protected dialogRef = inject(MatDialogRef<UserCreateModalComponent>);
    protected readonly manageUserTooltip = computed(() => {
        if (!this.canManageUser()) {
            return this.translocoService.translate('insufficientPermissions');
        }

        return '';
    });
    protected readonly roleService = inject(RoleService);
    protected readonly roleTooltip = computed(() => {
        if (!this.organizationAccessPermissionsService.canManageUser()) {
            return this.translocoService.translate('insufficientPermissions');
        }
        if (!this.connectServicesLicense()) {
            return this.translocoService.translate('onlyUserWithLicenceCanAssociateRole');
        }

        return '';
    });
    protected readonly snackbarService = inject(SnackBarService);
    protected readonly streetViewQuotaTooltipUi = computed(() => {
        if (!this.canManageUser()) {
            return 'insufficientPermissions';
        }

        return this.streetViewQuotaTooltip();
    });

    private readonly organizationAccessPermissionsService = inject(OrganizationAccessPermissionsService);
    private readonly peopleListBloc = inject(PeopleListBloc);
    private readonly projectSettingsPermissionsService = inject(ProjectSettingsPermissionsService);
    private readonly roleListBloc = inject(RoleListBloc);

    public closeModal(event: ModalResultEnum): void {
        event === ModalResultEnum.CONFIRM ? this.submitForm() : this.dialogRef.close();
    }

    public initForm(): FormGroup<UserFormType> {
        return new FormGroup<UserFormType>({
            firstName: new FormControl<string>('', {
                nonNullable: true,
                validators: [Validators.maxLength(FormService.VARCHAR_COMMON_LENGTH), Validators.required],
            }),
            licenses: new FormControl<License[]>([], {
                nonNullable: true,
            }),
            mail: new FormControl<string>('', {
                asyncValidators: [this.checkMailExistsValidator()],
                nonNullable: true,
                updateOn: 'change',
                validators: [Validators.email, Validators.required],
            }),
            name: new FormControl<string>('', {
                nonNullable: true,
                validators: [Validators.maxLength(FormService.VARCHAR_COMMON_LENGTH), Validators.required],
            }),
            projects: new FormControl<Project[]>([], {
                nonNullable: true,
            }),
            role: new FormControl<Role | null>(null, {
                nonNullable: !!this.connectServicesLicense(),
            }),
            streetViewQuota: new FormControl<number | null>(this.connectServicesLicense()?.streetViewQuota ?? null, {
                nonNullable: false,
                validators: [Validators.max(this.allocatableQuota() ?? 0)],
            }),
        });
    }

    public async ngOnInit(): Promise<void> {
        if (!this.securityService.meSignal()) {
            return;
        }

        this.allocatableQuota.set(this.securityService.meSignal()!.organization.streetViewQuota);

        this.formGroup.set(this.initForm());

        await this.initRoles();
        const projects = await this.fetchProjects();

        this.projects.set(projects.map((project: Project) => ({ label: project.name, value: project })));
    }

    public async submitForm(): Promise<void> {
        if (this.formGroup()!.invalid) {
            return;
        }

        await firstValueFrom(
            this.peopleService.personControllerPost({
                createPersonDto: {
                    email: this.formGroup()!.controls.mail.value,
                    familyName: this.formGroup()!.controls.name.value,
                    finalizeInvitationPath: `/${RoutePathEnum.FINALIZE_INVITATION}`,
                    givenName: this.formGroup()!.controls.firstName.value,
                    licenses: this.formGroup()!.controls.licenses.value.map((license: License) => license.id),
                    projects: this.formGroup()!.controls.projects.value!.map((project: Project) => project.id),
                    role:
                        this.formGroup()?.controls.role.value && this.connectServicesLicense()
                            ? this.formGroup()?.controls.role.value?.id
                            : undefined,
                    streetViewRequestsLimit: this.formGroup()?.controls.streetViewQuota.value ?? undefined,
                },
            }),
        );

        this.snackbarService.openSuccessSnackBar('userCreatedSuccess');
        await this.peopleListBloc.load({}, { refresh: true, useLastLoadParameters: true });
        await this.roleListBloc.load({}, { refresh: true, useLastLoadParameters: true });
        this.dialogRef.close();
    }

    private async initRoles(): Promise<void> {
        if (!this.roleListBloc.value()) {
            return;
        }
        const roles = await this.fetchRoles();

        const roleOptions: SelectOption<Role>[] = [];
        for (const role of roles) {
            const remainingRoleCount: number | null = (await this.roleService.remainingAssignmentRolesCount(role)) ?? 0;

            roleOptions.push({
                disabledReason:
                    !remainingRoleCount && role.maxNumberOfAssignments
                        ? this.translocoService.translate('unableToAssignRole')
                        : undefined,
                label: role.name,
                value: role,
            });
        }
        this.roles.set(roleOptions);
    }
}
